import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { requestJson } from '../lib/api';
import { useMenuPage } from '../hooks/useMenuPage';

const MIX_GROUPS = [
  { label: 'Easy-Digifit', color: '#38bdf8', test: (type) => type === 'Easy-Digifit' },
  { label: 'Classique', color: '#22c55e', test: (type) => type === 'Classique' },
  { label: 'V.S.', color: '#a78bfa', test: (type) => type === 'V.S.' },
  { label: 'Percées / gravure', color: '#ef4444', test: (type) => /Percées|Gravure/i.test(type) },
  {
    label: 'Autres (Clip, T.D., Trace…)',
    color: '#f59e0b',
    test: (type) => !['Easy-Digifit', 'Classique', 'V.S.'].includes(type) && !/Percées|Gravure/i.test(type),
  },
];

// Une variation de plus de 25 % par rapport à la période précédente est signalée.
const STORE_ALERT_DELTA = 25;

function formatNumber(value, digits = 0) {
  return Number(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function formatSigned(value, digits = 0) {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${formatNumber(Math.abs(value), digits)}`;
}

function getPercentChange(current, previous) {
  return previous ? ((current - previous) / previous) * 100 : null;
}

function formatDateInput(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function getMonthRange(year, month) {
  const firstDay = new Date(year, month, 1);
  const label = firstDay.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return {
    period: formatDateInput(firstDay).slice(0, 7),
    label: label.charAt(0).toUpperCase() + label.slice(1),
    from: formatDateInput(firstDay),
    to: formatDateInput(new Date(year, month + 1, 0)),
  };
}

function getPeriodLabel(period) {
  const from = new Date(period.from);
  const to = new Date(period.to);
  const isMonth = from.getUTCDate() === 1
    && from.getUTCMonth() === to.getUTCMonth()
    && new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth() + 1, 0)).getUTCDate() === to.getUTCDate();
  if (isMonth) {
    const label = from.toLocaleDateString('fr-FR', { month: 'short', timeZone: 'UTC' });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }
  const format = (date) => date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
  return `${format(from)} → ${format(to)}`;
}

function getMixCounts(types) {
  return MIX_GROUPS.map((group) =>
    Object.entries(types || {}).filter(([type]) => group.test(type)).reduce((sum, [, count]) => sum + count, 0)
  );
}

function KpiCard({ label, value, unit, delta, good, comparison, detail }) {
  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 950, lineHeight: 1.2 }}>
          {value}
          {unit && (
            <Typography component="span" sx={{ fontWeight: 850, color: 'text.secondary', ml: 0.5 }}>
              {unit}
            </Typography>
          )}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
          {delta && <Chip size="small" color={good ? 'success' : 'error'} label={delta} sx={{ fontWeight: 900 }} />}
          <Typography variant="caption" color="text.secondary">
            {comparison}
          </Typography>
        </Stack>
        {detail && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {detail}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function DailyChart({ period, byDay }) {
  const days = useMemo(() => {
    const list = [];
    const cursor = new Date(period.from);
    const end = new Date(period.to);
    while (cursor <= end) {
      const weekDay = cursor.getUTCDay();
      if (weekDay !== 0 && weekDay !== 6) {
        const key = cursor.toISOString().slice(0, 10);
        list.push({ key, count: byDay[key] || 0, monday: weekDay === 1, label: String(cursor.getUTCDate()) });
      }
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return list;
  }, [period, byDay]);

  if (!days.length) return null;
  const average = period.total / Math.max(period.workdays, 1);
  const max = Math.max(...days.map((day) => day.count), average, 1) * 1.1;

  return (
    <Box>
      <Box sx={{ position: 'relative', height: 220, display: 'flex', alignItems: 'flex-end', gap: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: `${(average / max) * 100}%`,
            borderTop: '2px dashed',
            borderColor: 'text.primary',
            opacity: 0.6,
            pointerEvents: 'none',
          }}
        />
        <Typography
          variant="caption"
          sx={{ position: 'absolute', right: 0, bottom: `calc(${(average / max) * 100}% + 2px)`, fontWeight: 900 }}
        >
          moyenne {formatNumber(average)} / jour
        </Typography>
        {days.map((day) => {
          const low = day.count < average * 0.35;
          return (
            <Tooltip key={day.key} title={`${day.label} : ${day.count} montages envoyés`}>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 8,
                  height: `${Math.max((day.count / max) * 100, day.count ? 1 : 0)}%`,
                  borderRadius: '4px 4px 0 0',
                  bgcolor: low ? 'warning.main' : '#38bdf8',
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
        {days.map((day) => (
          <Typography
            key={day.key}
            variant="caption"
            color={day.monday ? 'text.primary' : 'text.secondary'}
            sx={{ flex: 1, minWidth: 8, textAlign: 'center', fontWeight: day.monday ? 900 : 500, fontSize: 10.5 }}
          >
            {day.label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

export default function StatsPerformancePage() {
  const menuPage = useMenuPage('/statistiques/performance');
  const periodOptions = useMemo(() => {
    const today = new Date();
    return Array.from({ length: 24 }, (_item, index) => getMonthRange(today.getFullYear(), today.getMonth() - index));
  }, []);
  const [period, setPeriod] = useState(periodOptions[1].period);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sort, setSort] = useState({ key: 'total', direction: 'desc' });

  const selectedPeriod = periodOptions.find((option) => option.period === period) || periodOptions[1];

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const query = new URLSearchParams({ from: selectedPeriod.from, to: selectedPeriod.to }).toString();
      setData(await requestJson(`/statistics/performance?${query}`));
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const periods = useMemo(() => data?.periods || [], [data]);
  const current = periods[periods.length - 1];
  const previous = periods[periods.length - 2];

  const storeRows = useMemo(() => {
    if (!current) return [];
    const networkRate = current.total ? ((current.nc.NCA + current.nc.NCM) / current.total) * 100 : 0;
    return (data?.stores || []).map((store) => {
      const ncCount = store.nca + store.ncm;
      const ncRate = store.total ? (ncCount / store.total) * 100 : 0;
      return {
        ...store,
        delta: getPercentChange(store.total, store.previousTotal),
        share: current.total ? (store.total / current.total) * 100 : 0,
        ncCount,
        ncRate,
        ncAlert: ncCount > 0 && ncRate > networkRate * 2,
        mix: getMixCounts(store.types),
      };
    });
  }, [data, current]);

  const volumeRank = useMemo(
    () => new Map([...storeRows].sort((a, b) => b.total - a.total).map((store, index) => [store.storeSiteId, index + 1])),
    [storeRows]
  );

  const sortedStores = useMemo(() => {
    const direction = sort.direction === 'asc' ? 1 : -1;
    return [...storeRows].sort((a, b) => {
      if (sort.key === 'store') {
        return direction * String(a.storeCode).localeCompare(String(b.storeCode), 'fr', { numeric: true });
      }
      const aValue = a[sort.key] ?? -Infinity;
      const bValue = b[sort.key] ?? -Infinity;
      return direction * (aValue - bValue);
    });
  }, [storeRows, sort]);

  const handleSort = (key) => {
    setSort((currentSort) => ({
      key,
      direction: currentSort.key === key ? (currentSort.direction === 'desc' ? 'asc' : 'desc') : key === 'store' ? 'asc' : 'desc',
    }));
  };

  const watchList = useMemo(() => ({
    drops: storeRows.filter((store) => store.delta !== null && store.delta <= -STORE_ALERT_DELTA).sort((a, b) => a.delta - b.delta),
    rises: storeRows.filter((store) => store.delta !== null && store.delta >= STORE_ALERT_DELTA).sort((a, b) => b.delta - a.delta),
    nonConformities: storeRows.filter((store) => store.ncAlert).sort((a, b) => b.ncRate - a.ncRate),
  }), [storeRows]);

  const renderHeader = (key, label, align = 'right') => (
    <TableCell align={align} sortDirection={sort.key === key ? sort.direction : false}>
      <TableSortLabel
        active={sort.key === key}
        direction={sort.key === key ? sort.direction : 'desc'}
        onClick={() => handleSort(key)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const kpis = useMemo(() => {
    if (!current || !previous) return [];
    const perDay = (item) => item.total / Math.max(item.workdays, 1);
    const ncRate = (item) => (item.total ? ((item.nc.NCA + item.nc.NCM) / item.total) * 100 : 0);
    const complexRate = (item) => (item.total ? (item.complex / item.total) * 100 : 0);
    const leadDays = (item) => (item.leadTimeHours.median ?? 0) / 24;
    const previousLabel = getPeriodLabel(previous);
    const totalDelta = getPercentChange(current.total, previous.total);
    const perDayDelta = getPercentChange(perDay(current), perDay(previous));
    return [
      {
        label: 'Montages envoyés',
        value: formatNumber(current.total),
        delta: totalDelta === null ? '' : `${formatSigned(totalDelta)} %`,
        good: totalDelta >= 0,
        comparison: `${previousLabel} : ${formatNumber(previous.total)}`,
        detail: `dont ${formatNumber(current.adjusted)} facturables (Stats montage)`,
      },
      {
        label: 'Par jour ouvré',
        value: formatNumber(perDay(current)),
        delta: perDayDelta === null ? '' : `${formatSigned(perDayDelta)} %`,
        good: perDayDelta >= 0,
        comparison: `${previousLabel} : ${formatNumber(perDay(previous))}`,
        detail: `${current.workdays} jours ouvrés (lundi au vendredi)`,
      },
      {
        label: 'Délai atelier médian',
        value: formatNumber(leadDays(current), 1),
        unit: 'j',
        delta: `${formatSigned(leadDays(current) - leadDays(previous), 1)} j`,
        good: leadDays(current) <= leadDays(previous),
        comparison: `${previousLabel} : ${formatNumber(leadDays(previous), 1)} j`,
        detail: 'réception des verres → envoi magasin',
      },
      {
        label: 'Taux de non-conformité',
        value: formatNumber(ncRate(current), 2),
        unit: '%',
        delta: `${formatSigned(ncRate(current) - ncRate(previous), 2)} pt`,
        good: ncRate(current) <= ncRate(previous),
        comparison: `${previousLabel} : ${formatNumber(ncRate(previous), 2)} %`,
        detail: `${current.nc.NCA + current.nc.NCM} NC : ${current.nc.NCA} atelier, ${current.nc.NCM} magasin`,
      },
      {
        label: 'Montages complexes',
        value: formatNumber(complexRate(current), 1),
        unit: '%',
        delta: `${formatSigned(complexRate(current) - complexRate(previous), 1)} pt`,
        good: true,
        comparison: `${previousLabel} : ${formatNumber(complexRate(previous), 1)} %`,
        detail: `${current.complex} percées ou gravures`,
      },
    ];
  }, [current, previous]);

  const maxLeadDays = Math.max(1, ...periods.map((item) => (item.leadTimeHours.p90 ?? 0) / 24));

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 900 }}>
            {menuPage.sectionTitle}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            {menuPage.pageTitle}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            select
            label="Période"
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
            size="small"
            SelectProps={{ native: true }}
            sx={{ minWidth: 180 }}
          >
            {periodOptions.map((option) => (
              <option key={option.period} value={option.period}>
                {option.label}
              </option>
            ))}
          </TextField>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData}>
            Rafraîchir
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : current && previous && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mt: '8px !important' }}>
            {selectedPeriod.label} comparé à la période précédente. Un montage compte à la date où l'atelier valide l'envoi au magasin, comme dans Stats montage.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 2 }}>
            {kpis.map((kpi) => (
              <KpiCard key={kpi.label} {...kpi} />
            ))}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.6fr 1fr' }, gap: 2, alignItems: 'start' }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  Activité jour par jour
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Montages envoyés par jour ouvré (lundis en gras). En orange : jours quasi vides, signe que l'envoi est validé par lots.
                </Typography>
                <DailyChart period={current} byDay={data.byDay || {}} />
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  Délai atelier
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  De la réception des verres à l'envoi au magasin. Barre foncée : délai médian ; barre claire : 9 dossiers sur 10 partent avant.
                </Typography>
                <Stack spacing={1.25}>
                  {periods.map((item) => {
                    const median = (item.leadTimeHours.median ?? 0) / 24;
                    const p90 = (item.leadTimeHours.p90 ?? 0) / 24;
                    return (
                      <Box key={item.from} sx={{ display: 'grid', gridTemplateColumns: '90px minmax(0, 1fr) 64px', gap: 1.5, alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{getPeriodLabel(item)}</Typography>
                        <Tooltip title={`Médiane ${formatNumber(median, 1)} j · 9 sur 10 avant ${formatNumber(p90, 1)} j`}>
                          <Box sx={{ position: 'relative', height: 22, borderRadius: 1.5, bgcolor: 'action.hover' }}>
                            <Box sx={{ position: 'absolute', inset: 0, width: `${(p90 / maxLeadDays) * 100}%`, borderRadius: 1.5, bgcolor: 'primary.main', opacity: 0.25 }} />
                            <Box sx={{ position: 'absolute', inset: 0, width: `${(median / maxLeadDays) * 100}%`, borderRadius: 1.5, bgcolor: 'primary.main' }} />
                          </Box>
                        </Tooltip>
                        <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right' }}>{formatNumber(median, 1)} j</Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Box>

          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Magasins
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Les variations de plus de {STORE_ALERT_DELTA} % par rapport à la période précédente sont mises en couleur ; le taux de NC est en rouge au-delà du double de la moyenne réseau.
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ my: 1.5 }}>
                {MIX_GROUPS.map((group) => (
                  <Stack key={group.label} direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: 0.75, bgcolor: group.color }} />
                    <Typography variant="caption" color="text.secondary">{group.label}</Typography>
                  </Stack>
                ))}
              </Stack>
              <TableContainer>
                <Table size="small" sx={{ '& .MuiTableSortLabel-root': { fontWeight: 900 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="right">#</TableCell>
                      {renderHeader('store', 'Magasin', 'left')}
                      {renderHeader('total', getPeriodLabel(current))}
                      {renderHeader('previousTotal', getPeriodLabel(previous))}
                      {renderHeader('delta', 'Évolution')}
                      {renderHeader('share', 'Part réseau')}
                      {renderHeader('ncRate', 'NC')}
                      <TableCell>Mix</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedStores.map((store) => (
                      <TableRow key={store.storeSiteId} hover>
                        <TableCell align="right" sx={{ color: 'text.secondary' }}>{volumeRank.get(store.storeSiteId)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography component="span" sx={{ fontWeight: 900, fontSize: 'inherit' }}>{store.storeCode}</Typography>
                          <Typography component="span" color="text.secondary" sx={{ fontSize: 12, ml: 1 }}>
                            {String(store.storeName || '').replace(/^(ODB|AA|ACU)\s*-\s*/i, '')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 900 }}>{formatNumber(store.total)}</TableCell>
                        <TableCell align="right">{formatNumber(store.previousTotal)}</TableCell>
                        <TableCell align="right">
                          {store.delta === null ? (
                            <Chip size="small" label="nouveau" />
                          ) : (
                            <Chip
                              size="small"
                              label={`${formatSigned(store.delta)} %`}
                              color={store.delta <= -STORE_ALERT_DELTA ? 'error' : store.delta >= STORE_ALERT_DELTA ? 'success' : 'default'}
                              variant={Math.abs(store.delta) >= STORE_ALERT_DELTA ? 'filled' : 'outlined'}
                              sx={{ fontWeight: 900, minWidth: 64 }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">{formatNumber(store.share, 1)} %</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: store.ncAlert ? 900 : 400, color: store.ncAlert ? 'error.main' : 'inherit' }}>
                          {store.ncCount ? `${store.ncCount} · ${formatNumber(store.ncRate, 1)} %` : '—'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', width: 150, height: 12, borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover' }}>
                            {store.mix.map((count, index) => (
                              <Tooltip key={MIX_GROUPS[index].label} title={`${MIX_GROUPS[index].label} : ${count}`}>
                                <Box sx={{ width: `${store.total ? (count / store.total) * 100 : 0}%`, bgcolor: MIX_GROUPS[index].color }} />
                              </Tooltip>
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  Mix des montages
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Part de chaque type, comparée à la période précédente.
                </Typography>
                <Stack spacing={1.25}>
                  {[previous, current].map((item) => {
                    const counts = getMixCounts(item.types);
                    return (
                      <Box key={item.from} sx={{ display: 'grid', gridTemplateColumns: '90px minmax(0, 1fr)', gap: 1.5, alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{getPeriodLabel(item)}</Typography>
                        <Box sx={{ display: 'flex', height: 26, borderRadius: 1.5, overflow: 'hidden' }}>
                          {counts.map((count, index) => {
                            const share = item.total ? (count / item.total) * 100 : 0;
                            return (
                              <Tooltip key={MIX_GROUPS[index].label} title={`${MIX_GROUPS[index].label} : ${count} (${formatNumber(share, 1)} %)`}>
                                <Box
                                  sx={{
                                    width: `${share}%`,
                                    bgcolor: MIX_GROUPS[index].color,
                                    color: '#fff',
                                    fontSize: 11,
                                    fontWeight: 900,
                                    display: 'flex',
                                    alignItems: 'center',
                                    pl: 0.75,
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    borderRight: '2px solid',
                                    borderColor: 'background.paper',
                                  }}
                                >
                                  {share >= 9 ? `${formatNumber(share)} %` : ''}
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 1.5 }}>
                  À surveiller
                </Typography>
                <Stack spacing={1.25}>
                  {[
                    ['Magasins en recul de plus de 25 %', watchList.drops, (store) => `${store.storeCode} (${formatSigned(store.delta)} %)`],
                    ['Magasins en hausse de plus de 25 %', watchList.rises, (store) => `${store.storeCode} (${formatSigned(store.delta)} %)`],
                    ['Taux de NC au-delà du double de la moyenne', watchList.nonConformities, (store) => `${store.storeCode} (${store.ncCount} NC · ${formatNumber(store.ncRate, 1)} %)`],
                  ].map(([title, list, format]) => (
                    <Box key={title}>
                      <Typography variant="body2" sx={{ fontWeight: 850 }}>
                        {title} : {list.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {list.length ? list.map(format).join(', ') : 'Aucun.'}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </>
      )}
    </Stack>
  );
}
